import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Loader2, ArrowRight, Briefcase, ClipboardCheck, Target, ListChecks } from "lucide-react";

import SEO from "@/components/SEO";
import BreadcrumbNav from "@/components/BreadcrumbNav";
import CareerApplicationForm from "@/components/careers/CareerApplicationForm";
import JobListingCard from "@/components/careers/JobListingCard";
import { publicClient } from "@/lib/apiClient";
import { CAREER_CATEGORIES, CATEGORY_LABEL } from "@/data/careerTemplates";

const BREADCRUMBS = [{ label: "Home", to: "/" }, { label: "Careers" }];

const WHY_BLACK_RIDGE = [
  {
    title: "Real Construction Work",
    body: "You’ll be part of a hands-on general contractor team focused on roofing, remodeling, maintenance, and commercial scopes — not vague “handyman” gigs.",
  },
  {
    title: "Clear Structure & Standards",
    body: "Every role at Black Ridge runs off documented checklists, scopes, scripts, and standards. You always know what “good” looks like.",
  },
  {
    title: "Room to Grow",
    body: "We’re expanding across residential, commercial, and maintenance membership work. Strong performers can move into senior roles, leadership, or commission-heavy seats.",
  },
  {
    title: "Treated Like a Professional",
    body: "We invest in the people who show up, follow through, communicate, and protect the brand on every property.",
  },
];

const APPLICATION_STEPS = [
  { num: "01", title: "Submit Application", body: "Fill in your information, work history, and any resume or portfolio you have." },
  { num: "02", title: "Initial Review", body: "Black Ridge leadership reviews fit, location, and experience." },
  { num: "03", title: "Phone or Video Conversation", body: "If it looks like a fit, we set up a quick call to walk through expectations and pay." },
  { num: "04", title: "In-Person or Field Meeting", body: "For most roles we meet in person or in the field before extending an offer." },
  { num: "05", title: "Offer & Onboarding", body: "If hired, you’ll be onboarded onto our standards, scripts, and process." },
];

const POSITION_CATEGORY_BLURB = {
  sales:
    "Help homeowners and property owners understand their options for roofing, remodeling, maintenance memberships, and construction services.",
  superintendent:
    "Run jobsites, coordinate trades, protect quality, and keep schedules tight on residential and commercial projects.",
  "maintenance-tech":
    "Perform structured maintenance visits, inspections, photo documentation, and small approved repairs for membership clients.",
  "appointment-setter":
    "Work the phones and inbox to convert inbound interest into booked consultations and inspections.",
  "project-manager":
    "Manage scope, budget, schedule, subcontractors, and client communication from sale through closeout.",
  "roofing-sales":
    "Specialize in residential and commercial roofing — inspections, options, storm-related claims (without promises), and clean handoffs to production.",
  "admin-office":
    "Keep the back office running — leads, scheduling, documents, and clean communication with clients and team members.",
  "skilled-labor":
    "Bring a trade or general carpentry skillset to a structured GC environment.",
  "subcontractor-coordinator":
    "Run the subcontractor side — onboarding, scope, scheduling, and quality protection.",
};

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

/**
 * Careers (public) — `/careers`
 *
 * Page sections (per spec):
 *   1. Hero with CTAs
 *   2. Why Work With Black Ridge
 *   3. Open Positions (cards)
 *   4. Position Categories
 *   5. What We Look For
 *   6. How the Application Process Works
 *   7. Application Form (with prefill from listing CTA)
 */
export default function Careers() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [prefill, setPrefill] = useState(null);
  const formRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    publicClient
      .get("/careers")
      .then((res) => {
        if (cancelled) return;
        setListings(Array.isArray(res.data) ? res.data : []);
        setError("");
      })
      .catch(() => {
        if (cancelled) return;
        setError("We couldn’t load open positions right now. Please try again in a moment.");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredListings = useMemo(() => {
    if (categoryFilter === "all") return listings;
    return listings.filter((l) => l.category === categoryFilter);
  }, [listings, categoryFilter]);

  const handleApply = useCallback((listing) => {
    setPrefill({
      _stamp: Date.now(),
      listing_id: listing.id,
      position_title: listing.title,
      category: listing.category,
    });
    scrollToId("careers-apply");
  }, []);

  return (
    <>
      <SEO
        title="Construction Careers Austin TX | Black Ridge Construction"
        description="Apply for sales, superintendent, and maintenance technician opportunities with Black Ridge Construction in Austin and Central Texas."
      />

      {/* ───────────────────────── Hero ───────────────────────── */}
      <section className="bg-br-black text-white">
        <div className="container-br py-12 lg:py-20">
          <BreadcrumbNav dark items={BREADCRUMBS} />
          <div className="max-w-3xl mt-5">
            <div className="br-eyebrow text-br-bronze-2">Careers</div>
            <h1 className="br-headline text-4xl sm:text-5xl mt-3">Build With Black Ridge Construction</h1>
            <p className="mt-4 text-gray-300 leading-relaxed">
              Black Ridge Construction is growing across residential construction, roofing, remodeling, maintenance memberships, and commercial project work. We are looking for reliable, driven people who want structure, opportunity, and a clear path to grow with the company.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button type="button" onClick={() => scrollToId("open-positions")} className="br-btn-primary" data-testid="careers-hero-positions">
                <Briefcase size={14} /> View Open Positions
              </button>
              <button type="button" onClick={() => scrollToId("careers-apply")} className="br-btn-secondary" data-testid="careers-hero-apply">
                Apply Now <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────── Why Work With Black Ridge ────────────────────── */}
      <section className="section-pad bg-br-paper">
        <div className="container-br">
          <div className="max-w-2xl mb-8">
            <div className="br-eyebrow">Why Black Ridge</div>
            <h2 className="br-headline text-3xl sm:text-4xl mt-3">A Real GC. A Real Pipeline. A Real Path.</h2>
            <p className="mt-3 text-gray-700">
              Black Ridge Construction isn’t a flash-in-the-pan sales shop or a one-truck handyman. We run residential, commercial, roofing, and maintenance work under one brand with documented standards — and we hire accordingly.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {WHY_BLACK_RIDGE.map((item) => (
              <div key={item.title} className="bg-white border border-gray-200 rounded-lg p-5" data-testid={`why-card-${item.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <div className="w-10 h-10 rounded-md bg-br-paper-2 flex items-center justify-center text-br-bronze mb-3">
                  <ClipboardCheck size={18} />
                </div>
                <div className="font-display tracking-widest uppercase text-base text-br-black">{item.title}</div>
                <p className="mt-1.5 text-sm text-gray-700 leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────────────── Open Positions ──────────────────── */}
      <section className="section-pad bg-white" id="open-positions">
        <div className="container-br">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <div>
              <div className="br-eyebrow">Currently Hiring</div>
              <h2 className="br-headline text-3xl sm:text-4xl mt-3">Open Positions</h2>
              <p className="mt-2 text-sm text-gray-600 max-w-xl">{listings.length} open position{listings.length === 1 ? "" : "s"} — expand any listing to read the full role.</p>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="careers-chips">
              <button
                type="button"
                onClick={() => setCategoryFilter("all")}
                className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-widest font-semibold border ${categoryFilter === "all" ? "bg-br-bronze border-br-bronze text-white" : "bg-white border-gray-200 text-gray-700 hover:border-br-bronze"}`}
                data-testid="careers-chip-all"
              >
                All
              </button>
              {CAREER_CATEGORIES.map((c) => {
                const count = listings.filter((l) => l.category === c.slug).length;
                if (count === 0) return null;
                const isActive = categoryFilter === c.slug;
                return (
                  <button
                    key={c.slug}
                    type="button"
                    onClick={() => setCategoryFilter(c.slug)}
                    className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-widest font-semibold border ${isActive ? "bg-br-bronze border-br-bronze text-white" : "bg-white border-gray-200 text-gray-700 hover:border-br-bronze"}`}
                    data-testid={`careers-chip-${c.slug}`}
                  >
                    {c.label} <span className="ml-1 opacity-70">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-16 text-gray-500" data-testid="careers-loading">
              <Loader2 className="animate-spin mr-2" size={18} /> Loading open positions…
            </div>
          )}
          {!loading && error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3" data-testid="careers-error">{error}</div>
          )}
          {!loading && !error && filteredListings.length === 0 && (
            <div className="bg-br-paper border border-gray-200 rounded-lg p-10 text-center" data-testid="careers-empty">
              <div className="w-12 h-12 mx-auto rounded-full bg-white flex items-center justify-center text-br-bronze mb-3">
                <Briefcase size={20} />
              </div>
              <div className="font-display tracking-widest uppercase text-lg text-br-black">No open positions in this category right now</div>
              <p className="mt-2 text-sm text-gray-700 max-w-md mx-auto">
                We’re always interested in hearing from sales, superintendent, and maintenance professionals — submit a general application below and we’ll reach out if a role opens up.
              </p>
              <button type="button" onClick={() => scrollToId("careers-apply")} className="br-btn-dark mt-5" data-testid="careers-empty-apply">
                Submit a General Application <ArrowRight size={14} />
              </button>
            </div>
          )}
          {!loading && !error && filteredListings.length > 0 && (
            <div className="grid lg:grid-cols-2 gap-5">
              {filteredListings.map((l) => (
                <JobListingCard key={l.id} listing={l} onApply={handleApply} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ───────────────────── Position Categories ──────────────────── */}
      <section className="section-pad bg-br-paper">
        <div className="container-br">
          <div className="max-w-2xl mb-8">
            <div className="br-eyebrow">Categories</div>
            <h2 className="br-headline text-3xl sm:text-4xl mt-3">Position Categories</h2>
            <p className="mt-3 text-gray-700">Black Ridge hires across these tracks. Even if you don’t see a listing posted in your track, send a general application — we keep strong applicants on file.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CAREER_CATEGORIES.map((c) => (
              <div key={c.slug} className="bg-white border border-gray-200 rounded-lg p-5" data-testid={`category-card-${c.slug}`}>
                <div className="text-[10px] uppercase tracking-[0.3em] text-br-bronze font-bold">Track</div>
                <div className="font-display text-base tracking-widest uppercase mt-1 text-br-black">{c.label}</div>
                <p className="mt-2 text-sm text-gray-700 leading-relaxed">{POSITION_CATEGORY_BLURB[c.slug] || "Reach out for current scope and expectations."}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────────────── What We Look For ───────────────────── */}
      <section className="section-pad bg-white">
        <div className="container-br grid lg:grid-cols-[1fr_1.4fr] gap-10">
          <div>
            <div className="br-eyebrow">Standards</div>
            <h2 className="br-headline text-3xl sm:text-4xl mt-3">What We Look For</h2>
            <p className="mt-3 text-gray-700 leading-relaxed">
              We hire based on follow-through, communication, and respect for the homeowner and the property. Construction-specific experience is a plus — not a hard requirement for every track.
            </p>
          </div>
          <ul className="space-y-2" data-testid="what-we-look-for">
            {[
              "Reliable communication — returns calls, answers texts, follows up",
              "Respect for client property, time, and trust",
              "Ability to follow a checklist and documented process",
              "Honest, accurate documentation in the field",
              "Professional appearance, tools, and vehicle (where applicable)",
              "Comfort with photos, simple reporting, and client-facing summaries",
              "Pride of workmanship and willingness to escalate problems early",
              "Alignment with Black Ridge guardrails — no over-promising, no shortcut work",
            ].map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-gray-800">
                <Target size={14} className="text-br-bronze mt-1 shrink-0" /> {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ────────────────── Application Process ─────────────────── */}
      <section className="section-pad bg-br-black text-white">
        <div className="container-br">
          <div className="max-w-2xl mb-8">
            <div className="br-eyebrow text-br-bronze-2">Process</div>
            <h2 className="br-headline text-3xl sm:text-4xl mt-3 text-white">How the Application Process Works</h2>
            <p className="mt-3 text-gray-300">Short, transparent, no fluff. Here’s what to expect from application to offer.</p>
          </div>
          <ol className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="application-process">
            {APPLICATION_STEPS.map((s) => (
              <li key={s.num} className="bg-br-charcoal border border-br-line rounded-lg p-5" data-testid={`process-step-${s.num}`}>
                <div className="text-[10px] uppercase tracking-[0.3em] text-br-bronze-2 font-bold">Step {s.num}</div>
                <div className="font-display text-base tracking-widest uppercase mt-1 text-white">{s.title}</div>
                <p className="mt-2 text-sm text-gray-300 leading-relaxed">{s.body}</p>
              </li>
            ))}
          </ol>
          <div className="mt-8">
            <button type="button" onClick={() => scrollToId("careers-apply")} className="br-btn-primary" data-testid="process-cta-apply">
              <ListChecks size={14} /> Submit My Application <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </section>

      {/* ────────────────────── Application Form ───────────────────── */}
      <section className="section-pad bg-white">
        <div className="container-br">
          <CareerApplicationForm
            ref={formRef}
            prefill={prefill}
            listingMeta={prefill ? { title: prefill.position_title, category: prefill.category && CATEGORY_LABEL[prefill.category] } : null}
          />
        </div>
      </section>
    </>
  );
}
